import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';

import { RootState } from '../../store/store';
import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { fetchCategory, filterProduct } from '../../store/slices/productSlice';
import { subCategory, filterJSON, filterObj } from '../../types';
import { COLORS } from '../../utils/ColorsTheme';
import Notification from '../common/Notification';
import './_sidebar.scss';

const Sidebar = () => {
  const productCategory = useAppSelector((state: RootState) => state.product.categories);
  const products = useAppSelector((state: RootState) => state.product.products);
  const dispatch = useAppDispatch();
  const [categoryFilter, setCategoryFilter] = useState<Array<subCategory>>([]);
  const [filter, setFilter] = useState<filterObj>({ categoryId: [] });
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [showMessage, setMessage] = useState<string>('');
  const handleClose = () => {
    setMessage('');
  };
  const applyFilter = (categories: Array<subCategory>) => {
    const tempFilter: filterJSON = {
      ...filter,
      categoryId: categories.map((cat) => cat.id),
      products,
    };
    setFilter(tempFilter);
    dispatch(filterProduct(tempFilter));
  };
  const checkboxChange = (e: React.ChangeEvent<HTMLInputElement>, subItem: subCategory) => {
    let categories: Array<subCategory> = [...categoryFilter];
    if (e.target.checked) {
      categories.push(subItem);
    } else {
      categories = categories.filter((cat) => cat.id !== subItem.id);
    }
    setCategoryFilter(categories);
    applyFilter(categories);
  };
  useEffect(() => {
    dispatch(fetchCategory());
  }, []);
  return (
    <>
      {!!productCategory.length && (
        <>
          <div className='sidebar_category'>
            <div className='section-title'>
              <Typography
                variant='h4'
                component='h4'
                style={{ color: isDarkMode ? COLORS.darkModeColor : COLORS.lightModeColor }}
              >
                Categories
              </Typography>
            </div>
            <div className='category_accordian'>
              <div className='accordian'>
                {productCategory.map((item, index) => {
                  return (
                    <Accordion
                      defaultExpanded={true}
                      key={index}
                      sx={{
                        backgroundColor: isDarkMode
                          ? COLORS.defaultDarkMode
                          : COLORS.defaultLightMode,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel${index}-content`}
                        id={`panel${index}-header`}
                      >
                        <Typography>{item.category}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className='card-body'>
                          <ul>
                            {item.subCategory.map((subItem, ind) => {
                              return (
                                <li key={ind}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        onChange={(e) => checkboxChange(e, subItem)}
                                        checked={
                                          !!categoryFilter.find((cat) => cat.id === subItem.id)
                                        }
                                        data-testid={`category-id-${subItem.id}`}
                                      />
                                    }
                                    label={subItem.name}
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </div>
            </div>
          </div>
          <div className='sidebar_category'>
            <div className='section-title'>
              <Typography
                variant='h4'
                component='h4'
                style={{ color: isDarkMode ? COLORS.darkModeColor : COLORS.lightModeColor }}
              >
                Shop By Price
              </Typography>
            </div>
            <div>
              <div
                className='p-3'
                style={{
                  backgroundColor: isDarkMode ? COLORS.defaultDarkMode : COLORS.defaultLightMode,
                }}
              >
                <Stack spacing={2} direction='row' sx={{ mb: 1 }} alignItems='center'>
                  <Typography variant='h6' component='div'>
                    Price: ₹{filter?.price?.min || 0}-₹{filter?.price?.max || 0}
                  </Typography>
                </Stack>
                <Stack spacing={2} direction='row' sx={{ mb: 1 }} alignItems='center'>
                  <Typography variant='h6' component='div'>
                    Min:
                  </Typography>
                  <Slider
                    aria-label='Min'
                    data-testid='min-filter'
                    value={filter?.price?.min || 0}
                    defaultValue={0}
                    step={100}
                    min={0}
                    max={5000}
                    valueLabelDisplay='auto'
                    onChange={(e: Event, newValue: number | number[]) => {
                      setFilter({
                        ...filter,
                        price: {
                          ...filter?.price,
                          min: newValue,
                        },
                      });
                    }}
                  />
                </Stack>
                <Stack spacing={2} direction='row' sx={{ mb: 1 }} alignItems='center'>
                  <Typography variant='h6' component='div'>
                    Max:
                  </Typography>
                  <Slider
                    aria-label='Max'
                    data-testid='max-filter'
                    defaultValue={0}
                    value={filter?.price?.max || 0}
                    step={100}
                    min={0}
                    max={5000}
                    valueLabelDisplay='auto'
                    onChange={(e: Event, newValue: number | number[]) => {
                      setFilter({
                        ...filter,
                        price: {
                          ...filter?.price,
                          max: newValue,
                        },
                      });
                    }}
                  />
                </Stack>
                <Stack
                  spacing={2}
                  direction='row'
                  sx={{ mb: 1 }}
                  alignItems='center'
                  justifyContent='center'
                >
                  <Button
                    variant='contained'
                    color='success'
                    data-testid='apply-button'
                    onClick={() => {
                      const min = filter?.price?.min;
                      const max = filter?.price?.max;
                      setMessage('');
                      if (min && max) {
                        if (min > max) {
                          setTimeout(() => {
                            setMessage('Min Price should be less than Max Price!');
                          }, 100);

                          return;
                        }
                      } else if (min && !max) {
                        setTimeout(() => {
                          setMessage('Min Price should be less than Max Price!');
                        }, 100);
                        return;
                      }
                      const tempFilter: filterJSON = {
                        ...filter,
                        products,
                      };
                      setFilter(tempFilter);
                      dispatch(filterProduct(tempFilter));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                    Apply
                  </Button>
                </Stack>
              </div>
            </div>
            <Stack
              spacing={2}
              direction='row'
              sx={{ mb: 1, mt: 3 }}
              alignItems='center'
              justifyContent='center'
            >
              <Button
                variant='contained'
                color='error'
                data-testid='remove-all-button'
                onClick={() => {
                  setMessage('');
                  const tempFilter: filterJSON = {
                    categoryId: [],
                    products,
                  };
                  setCategoryFilter([]);
                  setFilter(tempFilter);
                  dispatch(filterProduct(tempFilter));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Remove All
              </Button>
            </Stack>
          </div>
        </>
      )}
      {!productCategory.length && <Skeleton variant='rounded' height={714} />}
      {showMessage !== '' && (
        <Notification showMessage={showMessage} handleClose={handleClose} isSuccess={false} />
      )}
    </>
  );
};

export default Sidebar;
